import { useDomainServices } from "@/providers/DomainServices";
import { AssociationType } from "@/shared/domain/association";
import { FileRecord, IngestionStatus } from "@/shared/domain/files";
import { TopicRecord, MessagePathRecord } from "@/shared/domain/topics";
import { Datasource } from "@/shared/state/visualization";
import {
  DatasetSource,
  FileSource,
} from "@/shared/state/visualization/schema/v1";

import { EphemeralWorkspaceState } from "./EphemeralWorkspaceState";

export interface WorkspaceData {
  files: EphemeralWorkspaceState["files"];
  topics: EphemeralWorkspaceState["topics"];
  messagePaths: EphemeralWorkspaceState["messagePaths"];
}

export class WorkspaceDataService {
  private domainServices: ReturnType<typeof useDomainServices>;

  constructor(domainServices: ReturnType<typeof useDomainServices>) {
    this.domainServices = domainServices;
  }

  async fetchWorkspaceData(
    datasources: Datasource[],
    abortController: AbortController,
  ): Promise<WorkspaceData> {
    const { files, datasets } = this.partitionDatasource(datasources);

    if (datasets.length > 1) {
      throw new Error(
        "Unable to visualize multiple datasets at once. Please select files from a single dataset or visualize each dataset separately.",
      );
    }

    if (datasets.length === 1) {
      return this.fetchDatasetWorkspaceData(
        datasets[0].value.datasetId,
        abortController,
      );
    }

    return this.fetchFileWorkspaceData(
      files.map((file) => file.value.fileId),
      abortController,
    );
  }

  private extractMessagePaths(topics: TopicRecord[]): MessagePathRecord[] {
    return topics.flatMap((topic) => topic.message_paths);
  }

  private async fetchDatasetWorkspaceData(
    datasetId: string,
    abortController: AbortController,
  ): Promise<WorkspaceData> {
    // First, get all files in the dataset
    const fileRecords = await this.domainServices.datasetService.listAllFiles(
      datasetId,
      {
        abortSignal: abortController.signal,
      },
    );

    const ingestedFileRecords = this.getIngestedFiles(fileRecords);

    const topicRecords = await this.fetchTopicsForFiles(
      this.domainServices,
      ingestedFileRecords.map((fileRecord) => fileRecord.file_id),
      abortController,
    );

    const messagePathsRecords = this.extractMessagePaths(topicRecords);

    return {
      files: ingestedFileRecords.sort((a, b) =>
        a.relative_path.localeCompare(b.relative_path, undefined, {
          sensitivity: "base",
        }),
      ),
      topics: topicRecords,
      messagePaths: messagePathsRecords,
    };
  }

  private async fetchFileWorkspaceData(
    fileIds: string[],
    abortController: AbortController,
  ): Promise<WorkspaceData> {
    const [fileRecords, topicRecords] = await Promise.all([
      Promise.all(
        fileIds.map((fileId) =>
          this.domainServices.files.getFileRecord(fileId, {
            abortSignal: abortController.signal,
          }),
        ),
      ),
      this.fetchTopicsForFiles(this.domainServices, fileIds, abortController),
    ]);

    const messagePathsRecords = this.extractMessagePaths(topicRecords);

    return {
      files: fileRecords.sort((a, b) =>
        a.relative_path.localeCompare(b.relative_path, undefined, {
          sensitivity: "base",
        }),
      ),
      topics: topicRecords,
      messagePaths: messagePathsRecords,
    };
  }

  private async fetchTopicsForFiles(
    domainServices: ReturnType<typeof useDomainServices>,
    fileIds: string[],
    abortController: AbortController,
  ): Promise<TopicRecord[]> {
    const topicRecordsPerFile = await Promise.all(
      fileIds.map((fileId) =>
        domainServices.topics.getTopicsByAssociation(
          {
            association_id: fileId,
            association_type: AssociationType.File,
          },
          {
            abortSignal: abortController.signal,
          },
        ),
      ),
    );
    return topicRecordsPerFile.flat();
  }
  private getIngestedFiles(fileRecords: FileRecord[]): FileRecord[] {
    return fileRecords.filter(
      (fileRecord) =>
        fileRecord.ingestion_status !== IngestionStatus.NotIngested,
    );
  }

  private partitionDatasource(datasources: Datasource[]): {
    files: FileSource[];
    datasets: DatasetSource[];
  } {
    return datasources.reduce<{
      files: FileSource[];
      datasets: DatasetSource[];
    }>(
      (data, datasource) => {
        if (datasource.type === "file") {
          data.files.push(datasource);
        } else {
          data.datasets.push(datasource);
        }
        return data;
      },
      {
        files: [],
        datasets: [],
      },
    );
  }
}
